import {
  createProject,
  deleteProject,
  findProject,
  getProjects,
  getProjectsParams,
  updateProject,
  UpdateProjectDto,
} from '@/api/project.api';
import { queryKeys } from '@/queryKeys';
import { ProjectFormData } from '@/types/projects/ProjectFormData';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const useGetProjects = (
  clientId: string,
  params: getProjectsParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: queryKeys.projects.list(clientId, params),
    queryFn: () => getProjects(clientId, params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? Boolean(clientId),
  });

export const useCreateProject = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProjectFormData) => createProject(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.listBase(clientId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
};

export const useUpdateProject = (clientId: string, projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectDto) =>
      updateProject(clientId, projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(clientId, projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.listBase(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
};

export const useFindProject = (clientId: string, projectId: string) =>
  useQuery({
    queryKey: queryKeys.projects.detail(clientId, projectId),
    queryFn: () => findProject(clientId, projectId),
  });

export const useDeleteProject = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => deleteProject(clientId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.listBase(clientId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
};
